/**
 * V3 — Mini dashboard par entité : KPIs ciblés (caisse, factures, BL, devis, effectif) + Top 5 clients.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { api } from './api';
import {
  Wallet, FileText, Truck, Briefcase, Users, AlertTriangle,
  TrendingUp, TrendingDown, CheckCircle2,
} from 'lucide-react';

const fmt = (n) => Math.round(Number(n || 0)).toLocaleString('fr-FR');
const ENTITIES = ['ROPS', 'NETBTP', 'CIS', 'NKEYI', 'R2S', 'EXCUISINE'];
const ENTITY_COLORS = {
  CIS: '#22c55e', ROPS: '#f59e0b', NETBTP: '#ef4444',
  NKEYI: '#8b5cf6', R2S: '#fb923c', EXCUISINE: '#6366f1',
};

export default function V3EntityDashboard() {
  const [entity, setEntity] = useState('ROPS');
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const { data: d } = await api.get('/api/v3/groupe/entity-dashboard', { params: { entity } });
      setData(d);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Erreur');
    } finally {
      setLoading(false);
    }
  }, [entity]);

  useEffect(() => { Promise.resolve().then(fetchData); }, [fetchData]);

  return (
    <div className="v3-page" data-testid="v3-entity-dashboard">
      <div className="v3-page-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h1 style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
            <span className="v3-entity-pill" style={{
              background: (ENTITY_COLORS[entity] || '#94a3b8') + '20',
              color: ENTITY_COLORS[entity] || '#94a3b8',
              fontSize: 16, padding: '4px 10px',
            }}>{entity}</span>
            Dashboard détaillé
          </h1>
          <p>Vue ciblée sur une seule entité · KPIs caisse, factures, BL, devis, effectif</p>
        </div>
        <div className="v3-filter-grp">
          <label>Entité</label>
          <select value={entity} onChange={(e) => setEntity(e.target.value)} data-testid="filter-entity">
            {ENTITIES.map(e => <option key={e} value={e}>{e}</option>)}
          </select>
        </div>
      </div>

      {err && <div className="v3-error">⚠️ {err}</div>}
      {loading && <div className="v3-empty">Chargement…</div>}

      {data && !loading && (
        <>
          <section className="v3-kpi-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
            <Kpi icon={<TrendingUp/>} label="Caisse entrées" value={fmt(data.kpis.caisse_entrees) + ' FCFA'} sub={`${data.kpis.caisse_n_ops} opérations`} accent="emerald" />
            <Kpi icon={<TrendingDown/>} label="Caisse sorties" value={fmt(data.kpis.caisse_sorties) + ' FCFA'} accent="amber" />
            <Kpi icon={<Wallet/>} label="Solde caisse" value={fmt(data.kpis.caisse_solde) + ' FCFA'} accent={data.kpis.caisse_solde >= 0 ? 'emerald' : 'amber'} />
            <Kpi icon={<Users/>} label="Effectif" value={data.kpis.effectif} sub="actifs" accent="cyan" />
          </section>

          <section className="v3-kpi-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginTop: 16 }}>
            <Kpi icon={<FileText/>} label="CA Factures TTC" value={fmt(data.kpis.fact_ca_ttc) + ' FCFA'} sub={`${data.kpis.fact_count} factures`} accent="cyan" />
            <Kpi icon={<CheckCircle2/>} label="Encaissé" value={fmt(data.kpis.fact_paye) + ' FCFA'} accent="emerald" />
            <Kpi icon={<AlertTriangle/>} label="Impayé" value={fmt(data.kpis.fact_solde_impaye) + ' FCFA'} sub={`${data.kpis.fact_impayees} factures`} accent="amber" />
            <Kpi icon={<Truck/>} label="BL en attente" value={data.kpis.bl_en_attente} sub={`${data.kpis.devis_ouverts} devis ouverts`} accent="cyan" />
          </section>

          <section className="v3-card" style={{ marginTop: 24 }} data-testid="top-5-clients">
            <div className="v3-card-head">
              <h3>Top 5 clients · {entity}</h3>
              <span className="v3-card-sub">CA TTC factures</span>
            </div>
            {data.top_5_clients.length === 0 ? (
              <div className="v3-empty">Aucun client encore.</div>
            ) : (
              <div className="v3-table-wrap">
                <table className="v3-table">
                  <thead>
                    <tr>
                      <th style={{ width: 50 }}>#</th>
                      <th>Client</th>
                      <th className="num">Factures</th>
                      <th className="num">CA TTC</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.top_5_clients.map((c, i) => (
                      <tr key={c.client} data-testid={`entity-top-client-${i + 1}`}>
                        <td className="mono strong">#{i + 1}</td>
                        <td>{c.client}</td>
                        <td className="num">{c.nb_factures}</td>
                        <td className="num strong" style={{ color: '#d6f32f' }}>{fmt(c.ca)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function Kpi({ icon, label, value, sub, accent }) {
  return (
    <div className={`v3-kpi v3-kpi-${accent}`}>
      <div className="v3-kpi-icon">{icon}</div>
      <div className="v3-kpi-body">
        <div className="v3-kpi-label">{label}</div>
        <div className="v3-kpi-value">{value}</div>
        {sub && <div className="v3-kpi-sub">{sub}</div>}
      </div>
    </div>
  );
}
